import { useTranslation } from "react-i18next";
import { useEffect } from "react";
import type { Locale } from "@assessly/contracts/locales";
import { isAdminHost } from "../lib/host.js";
import {
  LOCALE_ENDONYMS,
  SELECTABLE_LOCALES,
  htmlLangOf,
  isSelectableLocale,
  localeAfterProfile,
  storeLocale,
} from "../lib/locale.js";
import { readProfileOnce, retryUnsavedChoice, saveLocaleChoice } from "../lib/profile-locale.js";

/**
 * The control that makes a translated locale reachable, and the place the
 * product language meets the user's saved one (spec 16 §3.10, D-430).
 *
 * It appears in every shell on purpose. The office header is not enough: floor
 * operators never see it, and floor operators are the population the Russian
 * translation exists for.
 *
 * Options are ENDONYMS from the registry ("Русский", never "Russian"): someone
 * who cannot read the current UI language has to be able to find their own.
 * Only selectable locales are listed (SELECTABLE_LOCALES, derived from the
 * i18n-completeness.ts claim), so a locale whose dictionaries are still English
 * is never offered.
 *
 * A choice is stored on the device AND saved to the user's profile, so it
 * follows the user to another device and email follows it too. On mount the
 * switcher reads the profile once per signed-in user (lib/profile-locale.ts)
 * and applies the resolution order in lib/locale.ts: a language the user
 * saved elsewhere replaces this device's guess, unless the user has a choice
 * here the profile has not accepted yet, which it sends again. On the
 * platform console's host there is no tenant user, so it does neither.
 */

function onAdminHost(): boolean {
  try {
    return isAdminHost(window.location.hostname);
  } catch {
    return false;
  }
}

export function LocaleSwitcher({ className }: { className?: string }) {
  const { t, i18n } = useTranslation();
  const current: Locale = isSelectableLocale(i18n.language) ? i18n.language : "en";

  useEffect(() => {
    if (onAdminHost()) return;
    let live = true;
    const settle = () => {
      void readProfileOnce().then((profile) => {
        if (!live || profile === undefined) return;
        void retryUnsavedChoice(profile);
        const next = localeAfterProfile(profile, i18n.language);
        if (next === undefined) return;
        storeLocale(next);
        void i18n.changeLanguage(next);
      });
    };
    settle();
    // Back online: a choice made without signal goes to the profile now.
    window.addEventListener("online", settle);
    return () => {
      live = false;
      window.removeEventListener("online", settle);
    };
  }, [i18n]);

  // One locale means no choice to offer; the control would be furniture.
  if (SELECTABLE_LOCALES.length < 2) return null;

  return (
    <select
      className={className ?? "locale-switcher"}
      // The accessible name is the only part of this control that gets
      // translated; the options are endonyms.
      aria-label={t("nav.language")}
      data-testid="locale-switcher"
      value={current}
      onChange={(e) => {
        const next = e.target.value;
        if (!isSelectableLocale(next)) return;
        storeLocale(next);
        void i18n.changeLanguage(next);
        if (!onAdminHost()) void saveLocaleChoice(next);
      }}
    >
      {SELECTABLE_LOCALES.map((l: Locale) => (
        <option key={l} value={l} lang={htmlLangOf(l)}>
          {LOCALE_ENDONYMS[l]}
        </option>
      ))}
    </select>
  );
}
